import { Building2, MapPin, Phone, Mail, Globe, MoreVertical } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Company } from '../types/company.types';
import { COMPANY_STATUS_CONFIG } from '../types/company.types';

interface CompanyCardProps {
  company: Company;
  onEdit?: (company: Company) => void;
  onDelete?: (company: Company) => void;
  onClick?: (company: Company) => void;
}

export function CompanyCard({ company, onEdit, onDelete, onClick }: CompanyCardProps) {
  const statusConfig = COMPANY_STATUS_CONFIG[company.status];

  return (
    <Card
      className={`hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={() => onClick?.(company)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="p-2 bg-muted rounded-lg shrink-0">
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base truncate">{company.name}</h3>
                {company.status !== 'active' && (
                  <Badge className={`text-xs ${statusConfig.bgColor} ${statusConfig.color}`}>
                    {statusConfig.label}
                  </Badge>
                )}
              </div>
              {company.siret && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  SIRET: {company.siret}
                </p>
              )}
              {(company.city || company.postalCode) && (
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3" />
                  {company.postalCode} {company.city}
                </p>
              )}
            </div>
          </div>

          {(onEdit || onDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(company);
                    }}
                  >
                    Modifier
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(company);
                      }}
                    >
                      Supprimer
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Contact info */}
        {(company.phone || company.email || company.website) && (
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t text-sm">
            {company.phone && (
              <a
                href={`tel:${company.phone}`}
                className="flex items-center gap-1 text-muted-foreground hover:text-copper"
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="h-3 w-3" />
                {company.phone}
              </a>
            )}
            {company.email && (
              <a
                href={`mailto:${company.email}`}
                className="flex items-center gap-1 text-muted-foreground hover:text-copper"
                onClick={(e) => e.stopPropagation()}
              >
                <Mail className="h-3 w-3" />
                {company.email}
              </a>
            )}
            {company.website && (
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-muted-foreground hover:text-copper"
                onClick={(e) => e.stopPropagation()}
              >
                <Globe className="h-3 w-3" />
                Site web
              </a>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
